import { useState, useEffect } from "react";
import { ChakraProvider, Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody, Box, Text, Flex, VStack, Button, Progress, useToast } from "@chakra-ui/react";
import Custheme from "../NewPages/ChakraCustom.js";

const UploadManager = ({ projectId, files, apiFunc, onClose }) => {
  const isOpen = true;
  const [progressMap, setProgressMap] = useState({});
  const [statusMap, setStatusMap] = useState({}); 
  const [uploading, setUploading] = useState(true);
  const toast = useToast();

  const handleClose = () => { if (onClose) onClose(); };

  useEffect(() => {
    if (!files || files.length === 0) {
      setUploading(false);
      return;
    }

    const uploadFiles = async () => {
      let successCount = 0;

      for (let i = 0; i < files.length; i++) {
        const file = files[i];

        // Mark this file as uploading instantly
        setStatusMap(prev => ({ ...prev, [file.name]: 'uploading' }));

        try {
          const result = await apiFunc(projectId, [file], (percent) => {
            setProgressMap(prev => ({ ...prev, [file.name]: Math.min(100, Math.round(percent)) }));
          });

          if (result.data) {
            // Instantly mark success
            setStatusMap(prev => ({ ...prev, [file.name]: 'success' }));
            setProgressMap(prev => ({ ...prev, [file.name]: 100 }));
            successCount++;
          } else {
            // Instantly mark failed
            setStatusMap(prev => ({ ...prev, [file.name]: 'failed' }));
            setProgressMap(prev => ({ ...prev, [file.name]: 0 }));
          }

        } catch (err) {
          // Instantly mark failed on exception too
          setStatusMap(prev => ({ ...prev, [file.name]: 'failed' }));
          setProgressMap(prev => ({ ...prev, [file.name]: 0 }));
        }
      }

      setUploading(false);

      // Final toast shows only actual success count
      const failCount = files.length - successCount;
      if (successCount > 0 && failCount === 0) {
        toast({
          title: "Upload completed",
          description: `${successCount} file(s) uploaded successfully`,
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      } else if (successCount > 0 && failCount > 0) {
        toast({
          title: "Upload partially completed",
          description: `${successCount} succeeded, ${failCount} failed`,
          status: "warning",
          duration: 3000,
          isClosable: true,
        });
      } else {
        toast({
          title: "Upload failed",
          description: `All ${files.length} file(s) failed to upload`,
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    };

    uploadFiles();
  }, [files, projectId, apiFunc, toast]);

  return (
    <ChakraProvider theme={Custheme}>
      <Modal isOpen={isOpen} onClose={handleClose} isCentered size="sm">
        <ModalOverlay />
        <ModalContent minH="200px" maxH="80vh">
          <ModalHeader>Uploading Files</ModalHeader>
          <ModalCloseButton />
          <ModalBody overflowY="auto">
            <VStack spacing={4} align="stretch">
              {files.map((file) => {
                const status = statusMap[file.name];
                const progress = progressMap[file.name] || 0;

                return (
                  <Box
                    key={file.name}
                    padding="7px"
                    border="1px solid lightgray"
                    borderRadius="md"
                    shadow="lg"
                    mb={6}
                  >
                    <Text fontSize="md" fontWeight="500" wordBreak="break-word" mb={1}>
                      {file.name}
                    </Text>
                    <Text fontSize="sm" color="var(--text-secondary)" mb={1}>
                      size: {(file.size / 1024).toFixed(2)} KB
                    </Text>

                    {/* Status shown instantly per file */}
                    {!status && (
                      // Not started yet — waiting in queue
                      <Text fontSize="sm" color="gray.400">Waiting...</Text>
                    )}

                    {status === 'uploading' && (
                      <Flex alignItems="center" gap={2}>
                        <Progress
                          value={progress}
                          width="90%"
                          size="xs"
                          borderRadius="md"
                          colorScheme="orange"
                        />
                        <Text fontSize="sm">{progress}%</Text>
                      </Flex>
                    )}

                    {status === 'success' && (
                      <Text fontSize="sm" color="green.600" fontWeight="600">
                        Upload successful
                      </Text>
                    )}

                    {status === 'failed' && (
                      <Text fontSize="sm" color="red.600" fontWeight="600">
                        Upload failed
                      </Text>
                    )}
                  </Box>
                );
              })}
            </VStack>
          </ModalBody>

          {!uploading && (
            <Button m={4} p={4} onClick={handleClose} colorScheme="green">
              Close
            </Button>
          )}
        </ModalContent>
      </Modal>
    </ChakraProvider>
  );
};

export default UploadManager;
